import type { ProfileMediaKind } from "@veto/protocol";

export interface AvatarMedia {
  url: string;
  kind: ProfileMediaKind;
}

export function Avatar({
  name,
  size = 40,
  verified = false,
  media = null,
}: {
  name: string;
  size?: number;
  verified?: boolean;
  /** When present, renders the photo / GIF / muted looping video instead of initials. */
  media?: AvatarMedia | null;
}) {
  // Veto Liquid identity: avatars pick from a curated Apple-grade gradient set.
  // No purple, no green — blues, cyans, slates and warm neutrals only.
  const idx = [...name].reduce((a, c) => a + c.charCodeAt(0), 0) % AVATAR_GRADIENTS.length;
  const [from, to] = AVATAR_GRADIENTS[idx];
  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? "")
    .join("");
  return (
    <div className="avatar-wrap" style={{ width: size, height: size }}>
      <div
        className={`avatar${media ? " has-media" : ""}`}
        style={{
          width: size,
          height: size,
          fontSize: Math.max(12, Math.round(size * 0.36)),
          background: `linear-gradient(145deg, ${from}, ${to})`,
        }}
        aria-hidden
      >
        {media ? (
          media.kind === "video" ? (
            <video
              className="avatar-media"
              src={media.url}
              autoPlay
              loop
              muted
              playsInline
              disablePictureInPicture
            />
          ) : (
            <img
              className="avatar-media"
              src={media.url}
              alt=""
              loading="lazy"
              decoding="async"
              draggable={false}
            />
          )
        ) : (
          initials || "V"
        )}
      </div>
      {verified && <span className="avatar-badge" title="Verified" aria-label="Verified" />}
    </div>
  );
}

const AVATAR_GRADIENTS: readonly [string, string][] = [
  ["#5AB8FF", "#0A84FF"], // sky → blue
  ["#0A84FF", "#0255B8"], // blue → deep
  ["#A7C9FF", "#4D8DF0"], // steel
  ["#64C4FA", "#0A7AE0"], // cyan → blue
  ["#FF9F76", "#E86A4C"], // coral (warm accent)
  ["#8FA6BC", "#5A7288"], // slate
];
